//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import { ApiArrayModel } from 'src/app/core/models/model';
import PondSummaryReportModel from '../models/pond-summary.model';

/**
 * @service PondSummaryReportService
 * @description Service for retrieving aggregated pond summary reports within the aquaculture management system.
 * This service is responsible for fetching high-level performance and status data for ponds associated with specific farm sites.
 *
 * The summary reports typically include critical metrics such as:
 * - Biomass estimates
 * - Survival rates
 * - Feed conversion ratios (FCR)
 * - Average Body Weight (ABW)
 * - Days of Culture (DOC)
 *
 * This data is essential for farm managers and technicians to assess the overall health and productivity of a farm site at a glance.
 * The service interacts with the `/blubay/pondsummary/` backend endpoint to aggregate this data.
 *
 * **Key Responsibilities:**
 * 1. **Data Aggregation**: Fetching consolidated report data for all ponds in a farm site.
 * 2. **API Integration**: Managing the communication with the reporting microservice.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Generate summary reports for specific farm sites.
 * @feature 2. Support for array-based farm site filtering (though currently implemented for single site selection).
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<PondSummaryReportModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/pondsummary/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PondSummaryReportService extends Service<PondSummaryReportModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/pondsummary/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/pondsummary/');
  }

  /**
   * @method getPondSummaryReport
   * @async
   * @description Fetch the pond summary report for a specific farm site.
   *
   * Sends a POST request containing the farm site ID in an array format to the backend.
   * The backend aggregates data for all ponds within that site.
   *
   * @param {string} farmsitesId - The unique identifier of the farm site to generate the report for.
   * @returns {Promise<Array<PondSummaryReportModel>>} Promise resolving to an array of PondSummaryReportModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * pondSummaryReportService.getPondSummaryReport('site-123').then(report => {
   *   console.log('Site Summary:', report);
   * });
   */
  public getPondSummaryReport(farmsitesId: string) {
    return new Promise<Array<PondSummaryReportModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<PondSummaryReportModel>>(`${this.baseUrl}${this.serviceUrl}`, {
        farm_site_id: [farmsitesId]
      }, this.httpOptions).subscribe(response => {
        resolve(response.data);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
